import { type Ref, computed, ref } from 'vue';

import { HIDDEN, SHARING_TYPES } from '@/constants/data_shares';
import type { SegmentType } from '@/types/populations';

import type {
  PopulationFilterSelectionType,
  PopulationSharingLevel,
  SegmentSharingLevel,
  SharingPartnerRow,
} from '../types';

export type PartnerTabFilterValues = Record<string, (string | number)[]>;

export const PARTNER_FILTER = 'partner';
export const OWNER_FILTER = 'owner';
export const TAGS_FILTER = 'tags';
export const PARTNER_POPULATIONS_FILTER = 'partner_populations';
export const MY_POPULATIONS_FILTER = 'my_populations';

export type PopulationFiltersType = Record<
  typeof PARTNER_POPULATIONS_FILTER | typeof MY_POPULATIONS_FILTER,
  PopulationFilterSelectionType
>;

/** Checked populations with no level picked match wherever they are shared. */
const SHARED_LEVELS = Object.values(SHARING_TYPES).filter(
  (level) => level !== HIDDEN,
);

/**
 * A population missing from the row never matches. On the partner side, items
 * scoped to another partner are skipped, so a row with none in scope drops out.
 */
function matchesPopulationFilter(
  { operator, levels, items }: PopulationFilterSelectionType,
  segmentLevels: Record<SegmentType, SegmentSharingLevel>,
  populations: Record<number, PopulationSharingLevel>,
  partnerId?: number,
) {
  if (!levels.length && !items.length) return true;

  const scopedItems = items.filter(
    (item) =>
      !('organization_id' in item) ||
      !item.organization_id ||
      item.organization_id === partnerId,
  );

  const judgedLevels = items.length
    ? scopedItems.flatMap((item) =>
        'population_ids' in item && item.population_ids?.length
          ? item.population_ids.map((id) => populations[id]?.level ?? null)
          : [segmentLevels[item.segment]],
      )
    : Object.values(segmentLevels).filter((level) => level !== null);

  const acceptedLevels = levels.length ? levels : SHARED_LEVELS;
  function isAccepted(level: SegmentSharingLevel) {
    return level !== null && acceptedLevels.includes(level);
  }

  if (!judgedLevels.length) return false;
  return operator === 'AND'
    ? judgedLevels.every(isAccepted)
    : judgedLevels.some(isAccepted);
}

export default function usePartnerTabFilters(rows: Ref<SharingPartnerRow[]>) {
  const searchTerm = ref('');
  const activeFilters = ref<PartnerTabFilterValues>({});
  const populationFilters = ref<PopulationFiltersType | null>(null);

  /**
   * The search box narrows the partner name; the Partner filter picks specific
   * partners. They combine rather than widen, as do all the filters.
   */
  const filteredRows = computed(() => {
    let filtered = rows.value;

    if (searchTerm.value) {
      const search = searchTerm.value.toLowerCase();
      filtered = filtered.filter((row) =>
        row.partner.name.toLowerCase().includes(search),
      );
    }

    const partners = activeFilters.value[PARTNER_FILTER];
    if (partners?.length) {
      filtered = filtered.filter((row) => partners.includes(row.id));
    }

    const owners = activeFilters.value[OWNER_FILTER];
    if (owners?.length) {
      filtered = filtered.filter(
        (row) => !!row.owner && owners.includes(row.owner.user_id),
      );
    }

    const tags = activeFilters.value[TAGS_FILTER];
    if (tags?.length) {
      filtered = filtered.filter((row) =>
        row.tags.some((tag) => tags.includes(tag.id)),
      );
    }

    const populations = populationFilters.value;
    if (populations) {
      filtered = filtered.filter(
        (row) =>
          matchesPopulationFilter(
            populations[PARTNER_POPULATIONS_FILTER],
            row.incoming,
            row.incomingPopulations,
            row.id,
          ) &&
          matchesPopulationFilter(
            populations[MY_POPULATIONS_FILTER],
            row.outgoing,
            row.outgoingPopulations,
          ),
      );
    }

    return filtered;
  });

  function applySearch(value: string) {
    searchTerm.value = value;
  }

  function applyFilters(values: PartnerTabFilterValues) {
    activeFilters.value = values;
  }

  function applyPopulationFilters(values: PopulationFiltersType) {
    populationFilters.value = values;
  }

  return { filteredRows, applySearch, applyFilters, applyPopulationFilters };
}
